/**
 * Full-screen crop tool for a placed image. The author drags on the
 * ORIGINAL image to choose the region the placement shows; the region is
 * stored on the spread element (percent of the source image) and applied
 * cover-fit by SpreadImage / the PDF exporter. The original bank image is
 * never modified, and clearing the crop restores the full picture.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import type { SpreadImageCrop } from '@historyjki/shared';
import { Button } from '../ui/Button';
import { IconCheck, IconX, IconTrash } from './icons';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Smallest selectable crop side, in % of the image. */
const MIN_CROP = 4;

interface CropOverlayProps {
  src: string;
  caption?: string | null;
  initial: SpreadImageCrop | null;
  onApply: (crop: SpreadImageCrop | null) => void;
  onClose: () => void;
}

export function CropOverlay({ src, caption, initial, onApply, onClose }: CropOverlayProps) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<SpreadImageCrop | null>(initial);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  /** Pointer position in % of the displayed image (the frame shrink-wraps it). */
  const pctPoint = (e: PointerEvent | ReactPointerEvent) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return {
      x: clamp(((e.clientX - r.left) / r.width) * 100, 0, 100),
      y: clamp(((e.clientY - r.top) / r.height) * 100, 0, 100),
    };
  };

  const trackDrag = (
    e: ReactPointerEvent,
    onPoint: (p: { x: number; y: number }, start: { x: number; y: number }) => void,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const start = pctPoint(e);
    const move = (ev: PointerEvent) => onPoint(pctPoint(ev), start);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    onPoint(start, start);
  };

  /** Drag on the image draws a fresh selection. */
  const startDraw = (e: ReactPointerEvent) => {
    trackDrag(e, (p, s) => {
      setRect({
        x: Math.min(p.x, s.x),
        y: Math.min(p.y, s.y),
        w: Math.abs(p.x - s.x),
        h: Math.abs(p.y - s.y),
      });
    });
  };

  /** Drag inside the marquee moves it. */
  const startMove = (e: ReactPointerEvent) => {
    if (!rect) return;
    const o = { ...rect };
    trackDrag(e, (p, s) => {
      setRect({
        ...o,
        x: clamp(o.x + p.x - s.x, 0, 100 - o.w),
        y: clamp(o.y + p.y - s.y, 0, 100 - o.h),
      });
    });
  };

  /** Corner handles resize the marquee. */
  const startResize = (e: ReactPointerEvent, corner: string) => {
    if (!rect) return;
    const o = { ...rect };
    trackDrag(e, (p, s) => {
      let { x, y, w, h } = o;
      const dx = p.x - s.x;
      const dy = p.y - s.y;
      if (corner.includes('e')) w = clamp(o.w + dx, MIN_CROP, 100 - o.x);
      if (corner.includes('w')) {
        const nx = clamp(o.x + dx, 0, o.x + o.w - MIN_CROP);
        w = o.w + (o.x - nx);
        x = nx;
      }
      if (corner.includes('s')) h = clamp(o.h + dy, MIN_CROP, 100 - o.y);
      if (corner.includes('n')) {
        const ny = clamp(o.y + dy, 0, o.y + o.h - MIN_CROP);
        h = o.h + (o.y - ny);
        y = ny;
      }
      setRect({ x, y, w, h });
    });
  };

  const valid = !!rect && rect.w >= MIN_CROP && rect.h >= MIN_CROP;
  const apply = () => {
    if (!rect || !valid) return;
    // Selecting (nearly) everything means "no crop".
    const whole = rect.x < 0.5 && rect.y < 0.5 && rect.w > 99 && rect.h > 99;
    const round = (v: number) => Math.round(v * 100) / 100;
    onApply(
      whole ? null : { x: round(rect.x), y: round(rect.y), w: round(rect.w), h: round(rect.h) },
    );
  };

  const handles = ['nw', 'ne', 'sw', 'se'] as const;

  return createPortal(
    <div
      className="cropov"
      data-testid="crop-overlay"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="cropov__bar">
        <span className="cropov__title">Crop · {caption || 'image'}</span>
        <span className="cropov__hint">
          Drag on the picture to choose what the page shows — the original stays untouched
        </span>
        <div className="cropov__spacer" />
        {initial && (
          <Button variant="ghost" size="sm" icon={<IconTrash />} onClick={() => onApply(null)}>
            Remove crop
          </Button>
        )}
        <Button variant="secondary" size="sm" icon={<IconX />} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="accent" size="sm" icon={<IconCheck />} disabled={!valid} onClick={apply}>
          Apply crop
        </Button>
      </div>
      <div className="cropov__stage">
        <div className="cropov__frame" ref={frameRef} onPointerDown={startDraw}>
          <img src={src} alt={caption ?? ''} draggable={false} />
          {rect && (
            <div
              className="cropov__marquee"
              style={{
                left: `${rect.x}%`,
                top: `${rect.y}%`,
                width: `${rect.w}%`,
                height: `${rect.h}%`,
              }}
              onPointerDown={startMove}
            >
              {handles.map((c) => (
                <span
                  key={c}
                  className={`cropov__h cropov__h--${c}`}
                  onPointerDown={(e) => startResize(e, c)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
